import { useEffect, useState } from "react";
import { Navigate, Outlet, useParams } from "react-router-dom";
import { AppHeader } from "../components/AppHeader";
import { AppSidebar } from "../components/AppSidebar";
import { getMe, ApiError } from "../services/api";
import { useAuth } from "../services/auth-context";

/** Guards every /company/:slug/* route (Employee and Manager alike). Checks GET /me on mount so
 * a page refresh (not just a fresh login) correctly recognizes an existing, still-valid session
 * cookie — mirrors CoMemberLayout's own mount-time check, now that GET /me exists (T068/T069). */
export function CompanyLayout() {
  const { slug } = useParams<{ slug: string }>();
  const { auth, setAuth } = useAuth();
  const [checked, setChecked] = useState(auth?.role === "member" && auth.slug === slug);

  useEffect(() => {
    if (auth?.role === "member" && auth.slug === slug) {
      setChecked(true);
      return;
    }
    if (!slug) {
      setChecked(true);
      return;
    }
    getMe()
      .then((me) => {
        setAuth({ role: "member", slug, isManager: me.isManager, takesTest: me.takesTest });
        setChecked(true);
      })
      .catch((err) => {
        if (!(err instanceof ApiError)) throw err;
        setChecked(true); // not logged in — render() below redirects to this company's login
      });
    // Only re-check when the auth context hasn't already confirmed this company's member.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  if (!slug) return <Navigate to="/co-member/login" replace />;
  if (!checked) return null; // avoid a login-page flash while the check is in flight
  if (auth?.role !== "member" || auth.slug !== slug) {
    return <Navigate to={`/company/${slug}/login`} replace />;
  }

  return (
    <div className="flex h-full flex-col">
      <AppHeader companySlug={slug} />
      <div className="flex flex-1 overflow-hidden">
        <AppSidebar
          role={auth.isManager ? "manager" : "employee"}
          slug={slug}
          takesTest={auth.takesTest}
        />
        <main className="flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
